import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	concatMap,
	filter,
	from,
	map,
	mergeMap,
	of,
	switchMap,
	tap,
	throwError,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { AppSelector } from '../../core/store';
import { DataFrameRequester } from '../../requesters/core/data-frame.requester';
import { EntityRequester } from '../../requesters/entity/entity.requester';
import {
	EntityAction,
	EntityActionPopupComponent,
	IEntityActionModalData,
} from '../components/entity-action-popup/entity-action-popup.component';
import { EntityItemEntity } from '../components/entity-list/entity-item.entity';
import { EntityAdapter } from '../entity.adapter';
import {
	bulkDelete,
	constructEntityList,
	constructEntityListFail,
	constructEntityListSuccess,
	createDatablocksFromEntities,
	deleteEntity,
	deleteEntityFail,
	fetchEntityListFail,
	fetchEntityListSuccess,
	fetchPublicProjectDetails,
	fetchPublicProjectDetailsFail,
	fetchPublicProjectDetailsSuccess,
	navigateToCreateFromEntity,
	navigateToEntity,
	navigateToEntityCreate,
	navigateToEntityDuplicate,
	openCreateDatablocksConfirmationPopup,
	openDeleteConfirmationPopup,
	openPersistConfirmationPopup,
	openRepersistConfirmationPopup,
	openUnpersistConfirmationPopup,
	persistenceActionFinish,
	persistEntity,
	persistEntityFail,
	persistEntityStarted,
	persistEntityStopped,
	refreshEntityList,
	repersistEntity,
	repersistEntityFail,
	repersistEntityStarted,
	repersistEntityStopped,
	stopPersistence,
	toggleEntityStatus,
	trackAction,
	unpersistEntity,
	unpersistEntityFail,
	unpersistEntityStarted,
	unpersistEntityStopped, updateEntitiesFrameStatus, updateEntitiesFrameStatusSuccess,
	updateIncludePublic,
} from './entity.actions';
import { EntitySelector } from './entity.selector';
import {
	updateEntitiesCountForDepotFailure,
} from "../../depot/store";

type EntityJobSocketResponseType = {
	element_id: number;
	type: string;
	message: string;
	data:
		| {
				job_status: string;
				client_id: string;
				job_id: string;
				persisted: boolean;
				persistence_date: number;
				persistence_execution_time: number;
				persistence_size: number;
				current_repartition_number: number;
		  }
		| undefined;
};
@Injectable()
export class EntityEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToEntityCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToEntityCreate),
				tap(() => this.stateService.go(states.EntityCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToEntity = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToEntity),
				tap((a) =>
					this.stateService.go(states.EntityEdit, {
						entiteId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToEntityDuplicate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToEntityDuplicate),
				tap((a) =>
					this.stateService.go(states.EntityDuplicate, {
						duplicatedId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToCreateFromEntity = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToCreateFromEntity),
				tap((a) =>
					this.stateService.go(states.DatablockFromEntity, {
						entiteId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public constructEntityList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructEntityList),
			map(() =>
				constructEntityListSuccess({
					entityList: this.entityAdapter.generateEntityListStructure(),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					constructEntityListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getEntitiesFrameStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateEntitiesFrameStatus),
			switchMap((action) =>
				this.entityRequester.getEntitiesFrameStatus(
					action.ids,
					!action.shouldReload
				)
			),
			withLatestFrom(this.entitySelector.getEntityList$()),
			map(([response, currentList]) =>
				updateEntitiesFrameStatusSuccess({
					entityList: this.entityAdapter.updateListWithEntitiesFrameStatus(
						currentList,
						response
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateEntitiesCountForDepotFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public listenToWS$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructEntityListSuccess),
			switchMap(() =>
				from(
					this.ajsScopeService.listenOnRootScope<EntityJobSocketResponseType>(
						'job_global'
					)
				)
			),
			filter(
				(stompResponse) =>
					stompResponse?.type === 'PersistFrame' ||
					stompResponse?.type === 'UnPersistFrame' ||
					stompResponse?.type === 'RepersistFrame'
			),
			withLatestFrom(this.entitySelector.getEntityList$()),
			tap(([stompResponse, entityList]) => {
				if (stompResponse?.data?.job_status.toLocaleLowerCase() === 'running') {
					if (stompResponse?.type === 'PersistFrame') {
						this.store.dispatch(
							persistEntityStarted({
								entityId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'UnPersistFrame') {
						this.store.dispatch(
							unpersistEntityStarted({
								entityId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'RepersistFrame') {
						this.store.dispatch(
							repersistEntityStarted({
								entityId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
				} else if (
					stompResponse?.data?.job_status.toLocaleLowerCase() === 'error'
				) {
					const { elements } = entityList;
					const entity = elements
						.toList()
						.toArray()
						.find((e) => e.code === stompResponse?.message);
					if (!entity) {
						return;
					}

					const currentUrl = window.location.href;

					if (currentUrl.includes('/list')) {
						let errorMessage = '';
						if (stompResponse?.type === 'PersistFrame') {
							errorMessage = $localize`:i18n=@@entity.action.persist.error.message:
																			${entity.label}:ENTITY_NAME:`;
							this.store.dispatch(
								persistEntityStopped({
									entityId: entity.id,
									jobId: stompResponse.data.job_id,
								})
							);
						}

						if (stompResponse?.type === 'UnPersistFrame') {
							errorMessage = $localize`:i18n=@@entity.action.unpersist.error.message:
																			${entity.label}:ENTITY_NAME:`;
							this.store.dispatch(
								unpersistEntityStopped({
									entityId: entity.id,
									jobId: stompResponse.data.job_id,
								})
							);
						}

						if (stompResponse?.type === 'RepersistFrame') {
							errorMessage = $localize`:i18n=@@entity.action.repersist.error.message:
																			${entity.label}:ENTITY_NAME:`;
							this.store.dispatch(
								repersistEntityStopped({
									entityId: entity.id,
									jobId: stompResponse.data.job_id,
								})
							);
						}

						this.ngCommService.notifyOnError(errorMessage);
					}
				}
			}),
			map(([stompResponse]) => stompResponse),
			filter(
				(stompResponse) =>
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'running' &&
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'waiting'
			),
			map((stompResponse) =>
				persistenceActionFinish({
					entityId: stompResponse.element_id,
					persistenceInfo: {
						isPersisted: stompResponse.data?.persisted ?? false,
						persistenceDuration:
							stompResponse.data?.persistence_execution_time ?? 0,
						persistenceSize: stompResponse.data?.persistence_size ?? 0,
						persistenceDate: stompResponse.data?.persistence_date
							? new Date(stompResponse.data.persistence_date)
							: null,
						partitionCount: stompResponse.data?.current_repartition_number ?? 0,
					},
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshEntityList());
				return caught;
			})
		)
	);

	public initEntityList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructEntityListSuccess),
			withLatestFrom(this.entitySelector.includePublic$()),
			switchMap(([, includePublic]) =>
				this.entityRequester.getEntities(includePublic)
			),
			map((response) =>
				fetchEntityListSuccess({
					entityList: this.entityAdapter.generateEntityList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchEntityListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshEntityList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshEntityList),
			withLatestFrom(this.entitySelector.includePublic$()),
			switchMap(([, includePublic]) =>
				this.entityRequester.getEntities(includePublic, false)
			),
			map((response) =>
				fetchEntityListSuccess({
					entityList: this.entityAdapter.generateEntityList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchEntityListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateGetPublic$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateIncludePublic),
			map(() => refreshEntityList())
		)
	);

	public persistEntity$ = createEffect(() =>
		this.action$.pipe(
			ofType(persistEntity),
			concatMap((action) =>
				combineLatest([
					of(action.entityId),
					this.dataFrameRequester.persist(action.entityCode).pipe(
						catchError((error: HttpErrorResponse) =>
							throwError(() => ({
								error,
								entityLabel: action.entityLabel,
							}))
						)
					),
				])
			),
			map(([entityId, payload]) =>
				trackAction({
					entityId,
					jobId: payload.job_id,
				})
			),
			catchError(
				(
					customError: {
						error: HttpErrorResponse;
						entityLabel: string;
					},
					caught
				) => {
					const { error, entityLabel } = customError;
					console.error(error);
					if (window.location.href.includes('/list')) {
						const errorMessage = $localize`:i18n=@@entity.action.persist.error.message:
																			${entityLabel}:ENTITY_NAME:`;
						this.ngCommService.notifyOnError(errorMessage);
					}

					this.store.dispatch(
						persistEntityFail({
							error, // FIXME: stop spinner if fail..
						})
					);
					return caught;
				}
			)
		)
	);

	public repersistEntity$ = createEffect(() =>
		this.action$.pipe(
			ofType(repersistEntity),
			mergeMap((action) =>
				combineLatest([
					of(action.entityId),
					this.dataFrameRequester.repersist(action.entityCode).pipe(
						catchError((error: HttpErrorResponse) =>
							throwError(() => ({
								error,
								entityLabel: action.entityLabel,
							}))
						)
					),
				])
			),
			map(([entityId, payload]) =>
				trackAction({
					entityId,
					jobId: payload.job_id,
				})
			),
			catchError(
				(
					customError: { error: HttpErrorResponse; entityLabel: string },
					caught
				) => {
					const { error, entityLabel } = customError;
					console.error(error);
					if (window.location.href.includes('/list')) {
						const errorMessage = $localize`:i18n=@@entity.action.repersist.error.message:
																			${entityLabel}:ENTITY_NAME:`;
						this.ngCommService.notifyOnError(errorMessage);
					}
					this.store.dispatch(
						repersistEntityFail({
							error,
						})
					);
					return caught;
				}
			)
		)
	);

	public unpersistEntity$ = createEffect(() =>
		this.action$.pipe(
			ofType(unpersistEntity),
			concatMap((action) =>
				combineLatest([
					of(action.entityId),
					this.dataFrameRequester.unpersist(action.entityCode).pipe(
						catchError((error: HttpErrorResponse) =>
							throwError(() => ({
								error,
								entityLabel: action.entityLabel,
							}))
						)
					),
				])
			),
			map(([entityId, payload]) =>
				trackAction({
					entityId,
					jobId: payload.job_id,
				})
			),
			catchError(
				(
					customError: { error: HttpErrorResponse; entityLabel: string },
					caught
				) => {
					const { error, entityLabel } = customError;
					console.error(error);
					if (window.location.href.includes('/list')) {
						const errorMessage = $localize`:i18n=@@entity.action.unpersist.error.message:
																			${entityLabel}:ENTITY_NAME:`;
						this.ngCommService.notifyOnError(errorMessage);
					}
					this.store.dispatch(
						unpersistEntityFail({
							error, // FIXME: stop spinner if fail..
						})
					);
					return caught;
				}
			)
		)
	);

	public deleteEntity$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteEntity),
			switchMap((action) => this.entityRequester.deleteEntity(action.entityId)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@entity.delete.success:`
				);
				return refreshEntityList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@entity.delete.error:`
				);
				this.store.dispatch(deleteEntityFail());
				return caught;
			})
		)
	);

	public bulkDeleteEntities$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDelete),
			switchMap((action) => this.entityRequester.deleteMany(action.entityId)),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@entity.delete.multiple.success:`
				);
				return refreshEntityList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@entity.delete.multiple.error:`
				);
				this.store.dispatch(refreshEntityList());
				return caught;
			})
		)
	);

	public createDBsFromEntities$ = createEffect(() =>
		this.action$.pipe(
			ofType(createDatablocksFromEntities),
			switchMap((action) =>
				this.entityRequester.createDatablocksFromEntities(action.entityId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@entity.createDatablocks.success:`
				);
				return refreshEntityList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(' cannot create datablocks'); // FIXME
				this.store.dispatch(refreshEntityList());
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleEntityStatus),
			switchMap((action) => this.entityRequester.getEntity(action.entityId)),
			switchMap((entity) =>
				this.entityRequester.updateEntity({
					...entity,
					active: !entity.active,
				})
			),
			map(() => refreshEntityList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshEntityList());
				return caught;
			})
		)
	);

	public publicProjectDetails$ = createEffect(() =>
		this.action$.pipe(
			ofType(fetchPublicProjectDetails),
			switchMap((action) =>
				combineAll({
					entityId: action.entityId,
					charCount: this.entityRequester.getCharacteristicCountByEntityId(
						action.entityId
					),
					depotCount: this.entityRequester.getDepotCountByEntityId(
						action.entityId
					),
				})
			),
			map(({ charCount, depotCount, entityId }) =>
				fetchPublicProjectDetailsSuccess({
					characCount: charCount,
					depotCount,
					entityId,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchPublicProjectDetailsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openPersistConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openPersistConfirmationPopup),
				switchMap((action) => {
					const modalRef = this.modalService.openPopup<IEntityActionModalData>(
						EntityActionPopupComponent,
						{
							title: $localize`:i18n=@@entity.action.popup.persist.header:`,
							subTitle: '',
							entityId: action.entityId,
							element: action.entityLabel,
							action: EntityAction.Persist,
						},
						{
							disableClose: true,
						}
					);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				withLatestFrom(this.entitySelector.getEntityList$()),
				tap(([[action], list]) =>
					action.entityId
						.map((id) => list.elements.get(`${id}`))
						.filter((e): e is EntityItemEntity => e !== undefined)
						.forEach((e) => {
							if (e.persistenceState.isPersisted) {
								return this.store.dispatch(
									repersistEntity({
										entityId: e.id,
										entityCode: e.code,
										entityLabel: e.label,
									})
								);
							}
							return this.store.dispatch(
								persistEntity({
									entityId: e.id,
									entityCode: e.code,
									entityLabel: e.label,
								})
							);
						})
				)
			),
		{
			dispatch: false,
		}
	);

	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IEntityActionModalData>(
					EntityActionPopupComponent,
					{
						title: $localize`:i18n=@@entity.action.popup.delete.header:`,
						subTitle: '',
						entityId: action.entityId,
						element: action.entityLabel,
						action: EntityAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.entityId.length === 1) {
					return deleteEntity({
						entityId: action.entityId[0],
					});
				} else {
					return bulkDelete({
						entityId: action.entityId,
					});
				}
			})
		)
	);

	public openRepersistConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openRepersistConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IEntityActionModalData>(
					EntityActionPopupComponent,
					{
						title: $localize`:i18n=@@entity.action.popup.repersist.header:`,
						subTitle: '',
						entityId: action.entityId,
						element: action.entityLabel,
						action: EntityAction.Repersist,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			withLatestFrom(this.entitySelector.getEntityList$()),
			map(([[action], list]) =>
				repersistEntity({
					entityId: action.entityId,
					entityCode: (
						list.elements.get(action.entityId.toString()) as EntityItemEntity
					).code,
					entityLabel: action.entityLabel,
				})
			)
		)
	);

	public openUnpersistConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openUnpersistConfirmationPopup),
				switchMap((action) => {
					const modalRef = this.modalService.openPopup<IEntityActionModalData>(
						EntityActionPopupComponent,
						{
							title: $localize`:i18n=@@entity.action.popup.depersist.header:`,
							subTitle: '',
							entityId: action.entityId,
							element: action.entityLabel,
							action: EntityAction.Depersist,
						},
						{
							disableClose: true,
						}
					);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				withLatestFrom(this.entitySelector.getEntityList$()),
				tap(([[action], list]) => {
					action.entityId
						.map((id) => list.elements.get(`${id}`))
						.filter((e): e is EntityItemEntity => e !== undefined)
						.filter((e) => e.persistenceState.isPersisted)
						.forEach((e) =>
							this.store.dispatch(
								unpersistEntity({
									entityId: e.id,
									entityCode: e.code,
									entityLabel: e.label,
								})
							)
						);
				})
			),
		{
			dispatch: false,
		}
	);

	public stopOngoingPersistence$ = createEffect(() =>
		this.action$.pipe(
			ofType(stopPersistence),
			switchMap((action) =>
				this.entityRequester.stopPersistence(action.jobKey)
			),
			map(() => refreshEntityList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchPublicProjectDetailsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public openCreateDBsFormEntitiesConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openCreateDatablocksConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IEntityActionModalData>(
					EntityActionPopupComponent,
					{
						title: $localize`:i18n=@@entity.action.popup.createDatablock.header:`,
						subTitle: '',
						entityId: action.entityId,
						element: '',
						action: EntityAction.CreateDatablock,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) =>
				createDatablocksFromEntities({
					entityId: action.entityId,
				})
			)
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly entitySelector: EntitySelector,
		private readonly appSelector: AppSelector,
		private readonly entityRequester: EntityRequester,
		private readonly dataFrameRequester: DataFrameRequester,
		private readonly entityAdapter: EntityAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService,
		private readonly ajsScopeService: AjsScopeService
	) {}
}
